import React from 'react';
import { Star, Zap, Shield, CreditCard, AlertTriangle } from 'lucide-react';
import { CTAButton } from '../ui/CTAButton';
export function Hero() {
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative w-full min-h-[90vh] md:min-h-screen bg-gradient-to-b from-cream-bg to-cream-alt pt-12 md:pt-20 pb-12 md:pb-16 px-4 md:px-6 overflow-hidden" data-id="element-130">
      {/* Subtle gold glow background — hidden on small screens for mobile perf (reduces paint cost) */}
      <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold-accent/10 rounded-full blur-3xl pointer-events-none" data-id="element-131" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-132">
        {/* Attention Banner — renders synchronously for fast FCP */}
        <div className="flex justify-center mb-6" data-id="element-133">
          <div className="inline-flex items-center justify-center flex-wrap gap-2 bg-red-600 text-white px-4 md:px-5 py-2 md:py-2.5 rounded-full shadow-lg font-bold text-xs sm:text-sm md:text-base text-center" data-id="element-134">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" data-id="element-135" />
            <span data-id="element-136">
              ความสนใจ: อาจารย์ฟุตบอลที่เหนื่อยกับการนั่งหาแบบฝึกหัดทั้งคืน
            </span>
          </div>
        </div>

        {/* Main Headline — render synchronously (no fade) for fast FCP */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.15] font-display font-bold text-center text-navy-primary mb-4 md:mb-6 max-w-4xl mx-auto" data-id="element-137">
          วางแผนการสอนทั้งฤดูกาลในเวลาเพียงไม่กี่นาที
        </h1>

        {/* Subheadline — render synchronously for fast FCP */}
        <p className="text-base md:text-lg text-center text-gray-700 mb-8 md:mb-12 max-w-3xl mx-auto leading-relaxed px-2" data-id="element-138">
          ชุดการสอนฟุตบอล "พร้อมใช้งานทันที" ชุดแรก พร้อมอุปกรณ์ แบบฝึกหัด
          และแผนการฝึกที่เตรียมมาแล้ว ตั้งแต่กลุ่มเยาวชนไปจนถึงระดับสูง —
          คืนเวลาให้คุณ และยกระดับการสอนสู่ขั้นต่อไป
        </p>

        {/* Product Mockup — no entrance animation; the image IS the LCP so we want it painted ASAP */}
        {/* <picture>: mobile (≤768px) loads the smaller, lighter mockup; desktop loads the original. */}
        {/* Desktop visual is UNCHANGED — same source, same dimensions, same drop-shadow. */}
        {/* min-h-[340px] on mobile reserves vertical space BEFORE the image loads to eliminate CLS. */}
        <div className="flex justify-center mb-6 md:mb-8" data-id="element-139">
          <div className="relative w-full px-4 md:px-0 min-h-[340px] sm:min-h-[420px] md:min-h-[500px] flex items-center justify-center" data-id="element-140">
            <picture data-id="element-141">
              <source media="(max-width: 768px)" srcSet="https://cdn.magicpatterns.com/uploads/v68hK4CPKStUhPKx3o2iUK/mocap_futbool_%282%29_%281%29.webp" type="image/webp" data-id="element-142" />
              <img src="https://cdn.magicpatterns.com/uploads/buXD3KcbWgFp1MZw1bc7Qq/ChatGPT_Image_12_may_2026,_16_36_32-Photoroom.webp" alt="ฟุตบอล พร้อม - ระบบการสอนฟุตบอลสมบูรณ์แบบ" width={800} height={800} fetchPriority="high" decoding="sync" loading="eager" className="mx-auto max-w-full h-auto w-full max-h-[340px] sm:max-h-[420px] md:max-h-[500px] object-contain drop-shadow-2xl" data-id="element-143" />
            </picture>
          </div>
        </div>

        {/* Inline Rating + Urgency — renders synchronously for fast FCP */}
        <div className="flex flex-col items-center gap-3 mb-8" data-id="element-144">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-center" data-id="element-145">
            <div className="flex gap-1" data-id="element-146">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-gold-accent text-gold-accent drop-shadow" data-id="element-147" />)}
            </div>
            <span className="text-base md:text-lg font-semibold text-navy-primary" data-id="element-148">
              4.9/5{' '}
              <span className="text-gray-600 font-normal block sm:inline text-sm md:text-base mt-1 sm:mt-0" data-id="element-149">
                (รีวิวที่ยืนยันแล้วกว่า 1,200 รายการ)
              </span>
            </span>
          </div>

          <div className="inline-flex items-center gap-2 bg-navy-primary text-white px-4 md:px-5 py-1.5 md:py-2 rounded-full shadow-md" data-id="element-150">
            <span className="text-sm md:text-base" data-id="element-151">🔥</span>
            <span className="text-xs sm:text-sm md:text-base font-medium" data-id="element-152">
              อาจารย์ 67 ท่านเลือกระบบนี้วันนี้
            </span>
          </div>
        </div>

        {/* CTA — renders synchronously for fast FCP */}
        <div className="flex flex-col items-center gap-4 mb-8 w-full px-4 md:px-0" data-id="element-153">
          <CTAButton onClick={scrollToOffer} size="large" className="w-full sm:w-auto" data-id="element-154">
            ⚽ ใช่! ฉันต้องการระบบครบชุดนี้
          </CTAButton>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 text-xs md:text-sm text-gray-600" data-id="element-155">
            <span className="flex items-center gap-1" data-id="element-156">
              <Zap className="w-3.5 h-3.5 md:w-4 md:h-4 text-gold-accent" data-id="element-157" />
              เข้าถึงได้ทันที
            </span>
            <span className="flex items-center gap-1" data-id="element-158">
              <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 text-gold-accent" data-id="element-159" />
              รับประกัน 7 วัน
            </span>
            <span className="flex items-center gap-1" data-id="element-160">
              <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 text-gold-accent" data-id="element-161" />
              ชำระครั้งเดียว
            </span>
          </div>
        </div>

        {/* Trust Strip — renders synchronously for fast FCP */}
        <div className="text-center" data-id="element-162">
          <p className="text-navy-primary font-medium text-sm md:text-base" data-id="element-163">
            🇹🇭 แหล่งข้อมูลอันดับ 1 ที่อาจารย์ฟุตบอลไทยเลือกใช้
          </p>
        </div>
      </div>
    </section>;
}